import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import api, { getMatches, getDashboardChat, sendDashboardChat } from '../services/api';
import { 
  Home, Plus, Settings, Edit2, Eye, Users, Heart, 
  MapPin, Euro, Calendar, MoreVertical, Trash2, 
  ToggleLeft, ToggleRight, ArrowRight, Loader2,
  Building2, MessageSquare, ChevronRight, Sparkles,
  TrendingUp, Clock, Star, Zap, CheckCircle, X, Send,
  User, Mail, Briefcase, Wallet, Lock, Crown, Camera,
  HelpCircle, Info, Lightbulb, Headphones, LogOut, Menu
} from 'lucide-react';

const VerhuurderDashboardPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading, logout } = useAuth();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const chatEndRef = useRef(null);
  
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState({ totalListings: 0, totalViews: 0, totalInterested: 0, totalMatches: 0 });
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState(null);
  const [showMenu, setShowMenu] = useState(null);
  const [viewingTenant, setViewingTenant] = useState(null);
  const [showNavMenu, setShowNavMenu] = useState(false);
  
  // Premium check for verhuurders
  const hasPremium = user?.subscription === 'swipematch' || user?.trialEndDate;
  
  // Chat state
  const [interestedTenants, setInterestedTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [activeTab, setActiveTab] = useState('woningen'); // 'woningen' | 'berichten'
  
  // New: Listing-based message view state
  const [selectedListingForMessages, setSelectedListingForMessages] = useState(null);
  const [listingsWithInterest, setListingsWithInterest] = useState([]);
  
  // Profile editing state
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [profileData, setProfileData] = useState({
    businessName: '',
    kvkNumber: '',
    bio: '',
    avatar: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user?.userType !== 'verhuurder') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [isAuthenticated, authLoading, navigate, user]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [listingsRes, prefsRes, matchesRes] = await Promise.all([
        api.get('/my-listings').catch(() => ({ data: { listings: [] } })),
        api.get('/landlord/preferences').catch(() => ({ data: { preferences: null } })),
        getMatches().catch(() => ({ matches: [] }))
      ]);
      
      const myListings = listingsRes.data.listings || [];
      setListings(myListings);
      setPreferences(prefsRes.data.preferences);
      
      // Load profile data from user
      if (user) {
        setProfileData({
          businessName: user.businessName || '',
          kvkNumber: user.kvkNumber || '',
          bio: user.bio || '',
          avatar: user.avatar || ''
        });
      }
      
      // Get interested tenants from matches
      const tenants = matchesRes.matches || [];
      setInterestedTenants(tenants);
      
      // Group tenants by listing for the new view
      const listingsMap = {};
      myListings.forEach(listing => {
        listingsMap[listing.id] = {
          ...listing,
          interestedCount: 0,
          interested: []
        };
      });
      
      tenants.forEach(tenant => {
        const listingId = tenant.listing?.id;
        if (listingId && listingsMap[listingId]) {
          listingsMap[listingId].interestedCount++;
          listingsMap[listingId].interested.push(tenant);
        }
      });
      
      // Sort by interested count descending
      const sortedListings = Object.values(listingsMap).sort((a, b) => b.interestedCount - a.interestedCount);
      setListingsWithInterest(sortedListings);
      
      const totalViews = myListings.reduce((sum, l) => sum + (l.views || 0), 0);
      
      setStats({
        totalListings: myListings.length,
        totalViews,
        totalInterested: tenants.length,
        totalMatches: tenants.length
      });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChat = async (tenant) => {
    setSelectedTenant(tenant);
    setLoadingChat(true);
    setChatMessages([]);
    
    try {
      const listingId = tenant.listing?.id;
      const otherUserId = tenant.tenant?.id;
      
      if (listingId && otherUserId) {
        const res = await getDashboardChat(listingId, otherUserId);
        setChatMessages(res.messages || []);
      }
    } catch (error) {
      console.error('Error loading chat:', error);
      setChatMessages([]);
    } finally {
      setLoadingChat(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTenant) return;
    
    setSendingMessage(true);
    try {
      const listingId = selectedTenant.listing?.id;
      const receiverId = selectedTenant.tenant?.id;
      
      if (!listingId || !receiverId) return;
      
      const res = await sendDashboardChat(selectedTenant.id, listingId, receiverId, newMessage);
      if (res.success && res.message) {
        setChatMessages(prev => [...prev, res.message]);
        setNewMessage('');
      }
    } catch (error) {
      toast({ title: 'Fout bij versturen', variant: 'destructive' });
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle avatar upload for landlord
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Alleen afbeeldingen toegestaan', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Maximaal 5MB toegestaan', variant: 'destructive' });
      return;
    }
    
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/upload/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.url) {
        setProfileData(prev => ({ ...prev, avatar: response.data.url }));
        toast({ title: 'Profielfoto geüpload!' });
      }
    } catch (error) {
      toast({ title: 'Upload mislukt', variant: 'destructive' });
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Save landlord profile (KVK, business name, bio)
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch('/user/settings', {
        businessName: profileData.businessName,
        kvkNumber: profileData.kvkNumber,
        bio: profileData.bio,
        avatar: profileData.avatar
      });
      toast({ title: 'Profiel opgeslagen!' });
      setShowProfileEdit(false);
    } catch (error) {
      toast({ title: 'Opslaan mislukt', variant: 'destructive' });
    } finally {
      setSavingProfile(false);
    }
  };

  const toggleListingActive = async (listingId, currentActive) => {
    try {
      await api.patch(`/listings/${listingId}`, { active: !currentActive });
      setListings(prev => prev.map(l => 
        l.id === listingId ? { ...l, active: !currentActive } : l
      ));
      toast({ 
        title: currentActive ? 'Advertentie gepauzeerd' : 'Advertentie geactiveerd',
        description: currentActive ? 'Je ontvangt geen nieuwe reacties' : 'Huurders kunnen je woning weer vinden'
      });
    } catch (error) {
      toast({ title: 'Er ging iets mis', variant: 'destructive' });
    }
    setShowMenu(null);
  };

  const deleteListing = async (listingId) => {
    if (!window.confirm('Weet je zeker dat je deze advertentie wilt verwijderen?')) return;
    
    try {
      await api.delete(`/listings/${listingId}`);
      setListings(prev => prev.filter(l => l.id !== listingId));
      toast({ title: 'Advertentie verwijderd' });
    } catch (error) {
      toast({ title: 'Er ging iets mis', variant: 'destructive' });
    }
    setShowMenu(null);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffHours < 1) return 'Nu';
      if (diffHours < 24) return `${diffHours}u geleden`;
      if (diffDays < 7) return `${diffDays}d geleden`;
      return date.toLocaleDateString('nl-NL');
    } catch { return ''; }
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const getImageUrl = (img) => {
    if (!img) return 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400';
    if (img.startsWith('http')) return img;
    return `${API_URL}${img}`;
  };

  // Loading
  if (authLoading || loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#1F3A5F] to-[#0D2137] flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse">
            <Building2 className="w-10 h-10 text-white" />
          </div>
          <p className="text-white/60">{language === 'nl' ? 'Dashboard laden...' : 'Loading dashboard...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#F7F7F7] flex flex-col" data-testid="verhuurder-dashboard">
      <Toaster />

      {/* Chat Modal */}
      {selectedTenant && (
        <div className="fixed inset-0 z-50 bg-[#F7F7F7] flex flex-col">
          {/* Chat Header */}
          <div className="bg-white border-b border-[#E5E5E5] px-4 py-3 safe-area-top">
            <div className="flex items-center gap-3 max-w-2xl mx-auto">
              <button 
                onClick={() => setSelectedTenant(null)}
                className="w-10 h-10 rounded-full bg-[#F7F7F7] flex items-center justify-center hover:bg-[#E5E5E5] transition-colors"
                data-testid="close-chat-btn"
              >
                <X className="w-5 h-5 text-[#2A2A2A]" />
              </button>
              
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F] rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {selectedTenant.tenant?.avatar || 'U'}
                  </div>
                  {selectedTenant.tenant?.isPremium && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-[#FFD700] rounded-full flex items-center justify-center border-2 border-white">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#2A2A2A] truncate">{selectedTenant.tenant?.name}</span>
                    {selectedTenant.tenant?.isPremium && (
                      <span className="inline-flex items-center gap-1 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0">
                        <CheckCircle className="w-2.5 h-2.5" />
                        {language === 'nl' ? 'Geverifieerd' : 'Verified'}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-[#2E7D32]">{selectedTenant.tenant?.profileType}</div>
                </div>
              </div>

              <div className="text-right hidden sm:block">
                <div className="text-xs text-[#9E9E9E]">Interesse in:</div>
                <div className="text-sm font-medium text-[#2A2A2A] truncate max-w-[150px]">{selectedTenant.listing?.title}</div>
              </div>
            </div>
          </div>

          {/* Tenant Info Bar */}
          <div className="bg-[#2E7D32]/5 border-b border-[#2E7D32]/20 px-4 py-2">
            <div className="max-w-2xl mx-auto flex items-center gap-4 text-sm overflow-x-auto">
              {selectedTenant.tenant?.budget && (
                <div className="flex items-center gap-1.5 text-[#2A2A2A] whitespace-nowrap">
                  <Wallet className="w-4 h-4 text-[#2E7D32]" />
                  <span>{selectedTenant.tenant.budget}</span>
                </div>
              )}
              {selectedTenant.tenant?.email && (
                <div className="flex items-center gap-1.5 text-[#2A2A2A] whitespace-nowrap">
                  <Mail className="w-4 h-4 text-[#2E7D32]" />
                  <span>{selectedTenant.tenant.email}</span>
                </div>
              )}
            </div>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="max-w-2xl mx-auto space-y-3">
              {/* Match indicator */}
              <div className="text-center py-3">
                <span className="inline-flex items-center gap-2 bg-[#2E7D32]/10 text-[#2E7D32] px-4 py-2 rounded-full text-sm font-medium">
                  <Heart className="w-4 h-4 fill-current" /> 
                  {selectedTenant.tenant?.name} heeft interesse in jouw woning
                </span>
                <p className="text-xs text-[#9E9E9E] mt-2">Matched {formatTime(selectedTenant.matchedAt)}</p>
              </div>

              {loadingChat ? (
                <div className="text-center py-8 text-[#9E9E9E]">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Berichten laden...
                </div>
              ) : chatMessages.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-[#F7F7F7] rounded-full flex items-center justify-center mx-auto mb-3">
                    <MessageSquare className="w-8 h-8 text-[#9E9E9E]" />
                  </div>
                  <p className="text-[#9E9E9E]">Nog geen berichten</p>
                  <p className="text-sm text-[#9E9E9E] mt-1">Stuur een bericht om contact te maken!</p>
                </div>
              ) : (
                chatMessages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.sender === 'me' 
                        ? 'bg-[#2E7D32] text-white rounded-br-sm' 
                        : 'bg-white text-[#2A2A2A] rounded-bl-sm border border-[#E5E5E5]'
                    }`}>
                      <p className="text-sm">{msg.content || msg.text}</p>
                      <p className={`text-xs mt-1 ${msg.sender === 'me' ? 'text-white/60' : 'text-[#9E9E9E]'}`}>{msg.time}</p>
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>
          </div>

          {/* Chat Input or Paywall */}
          <div className="bg-white border-t border-[#E5E5E5] p-4 safe-area-bottom">
            <div className="max-w-2xl mx-auto">
              {hasPremium ? (
                <div className="flex gap-3">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={language === 'nl' ? 'Typ je bericht...' : 'Type your message...'}
                    className="flex-1 h-12 bg-[#F7F7F7] border-0 rounded-full px-5 text-[#2A2A2A]"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    data-testid="chat-input"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="w-12 h-12 bg-[#2E7D32] rounded-full flex items-center justify-center disabled:opacity-50 hover:bg-[#256B2B] transition-colors"
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Send className="w-5 h-5 text-white" />
                    )}
                  </button>
                </div>
              ) : (
                /* Premium Required Paywall for Verhuurders */
                <div className="bg-gradient-to-r from-[#2E7D32]/10 to-[#1F3A5F]/10 rounded-2xl p-4 sm:p-5 border-2 border-dashed border-[#2E7D32]/30">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-r from-[#2E7D32] to-[#1F3A5F] rounded-full flex items-center justify-center flex-shrink-0">
                      <Lock className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-black text-[#2A2A2A] text-sm sm:text-base leading-tight">
                        {language === 'nl' ? '💬 Premium vereist om te chatten' : '💬 Premium required to chat'}
                      </p>
                      <p className="text-xs sm:text-sm text-[#9E9E9E] mt-1">
                        {language === 'nl' ? 'Upgrade om met geïnteresseerde huurders te praten' : 'Upgrade to chat with interested renters'}
                      </p>
                    </div>
                  </div>

                  {/* Premium benefits */}
                  <div className="bg-white rounded-xl p-3 mb-4 space-y-2">
                    <p className="text-xs font-bold text-[#2A2A2A] uppercase tracking-wide mb-2">
                      {language === 'nl' ? 'Met Premium krijg je:' : 'With Premium you get:'}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1.5 text-[#2A2A2A]">
                        <span className="text-[#2E7D32]">✓</span> 
                        {language === 'nl' ? 'Onbeperkt chatten' : 'Unlimited chat'}
                      </div>
                      <div className="flex items-center gap-1.5 text-[#2A2A2A]">
                        <span className="text-[#2E7D32]">✓</span> 
                        {language === 'nl' ? 'Direct contact' : 'Direct contact'}
                      </div>
                      <div className="flex items-center gap-1.5 text-[#2A2A2A]">
                        <span className="text-[#2E7D32]">✓</span> 
                        {language === 'nl' ? 'Woning boost' : 'Property boost'}
                      </div>
                      <div className="flex items-center gap-1.5 text-[#2A2A2A]">
                        <span className="text-[#2E7D32]">✓</span> 
                        {language === 'nl' ? 'Meer zichtbaarheid' : 'More visibility'}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/premium')}
                    className="w-full bg-gradient-to-r from-[#2E7D32] to-[#1F3A5F] text-white font-bold py-3 sm:py-4 rounded-full flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-lg text-sm sm:text-base"
                    data-testid="chat-paywall-upgrade-btn"
                  >
                    <Crown className="w-5 h-5" />
                    {language === 'nl' ? 'Upgrade naar Premium - €4,99/mnd' : 'Upgrade to Premium - €4.99/mo'}
                  </button>
                  <p className="text-center text-xs text-[#9E9E9E] mt-2">
                    {language === 'nl' ? '✨ Direct opzegbaar • Bereik meer huurders' : '✨ Cancel anytime • Reach more renters'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Dashboard */}
      <div className={`flex-1 flex flex-col overflow-hidden ${selectedTenant ? 'hidden' : ''}`}>
        {/* Header - Compact & Professional */}
        <div className="bg-gradient-to-br from-[#1F3A5F] via-[#1F3A5F] to-[#0D2137] pt-safe relative flex-shrink-0">
          <div className="absolute inset-0 opacity-20 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#2E7D32] rounded-full blur-[100px]"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => navigate('/')} className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
                <Home className="w-5 h-5 text-white" />
              </button>
              <SwipeMatchLogo size="small" variant="white" />
              <div className="relative">
                <button 
                  onClick={() => setShowNavMenu(!showNavMenu)} 
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                  <Menu className="w-5 h-5 text-white" />
                </button>
                
                {/* Navigation Menu Dropdown */}
                {showNavMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowNavMenu(false)} />
                    <div className="absolute right-0 top-12 z-50 w-64 bg-white rounded-2xl shadow-2xl border border-[#E5E5E5] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 max-h-[80vh] overflow-y-auto">
                      <div className="p-2 space-y-1">
                        <button
                          onClick={() => { navigate('/verhuurder/account'); setShowNavMenu(false); }}
                          className="flex items-center w-full px-4 py-3 rounded-xl text-[#2A2A2A] hover:bg-[#F7F7F7] transition-colors"
                        >
                          <User className="w-5 h-5 mr-3 text-[#2E7D32]" />
                          <span className="font-medium">{language === 'nl' ? 'Mijn profiel' : 'My profile'}</span>
                        </button>
                        <button
                          onClick={() => { navigate('/verhuurder/onboarding'); setShowNavMenu(false); }}
                          className="flex items-center w-full px-4 py-3 rounded-xl text-[#2A2A2A] hover:bg-[#F7F7F7] transition-colors"
                        >
                          <Plus className="w-5 h-5 mr-3 text-[#2E7D32]" />
                          <span className="font-medium">{language === 'nl' ? 'Woning plaatsen' : 'Add property'}</span>
                        </button>
                        <div className="border-t border-[#E5E5E5] my-2" />
                        <button
                          onClick={() => { navigate('/hoe-werkt-het'); setShowNavMenu(false); }}
                          className="flex items-center w-full px-4 py-3 rounded-xl text-[#2A2A2A] hover:bg-[#F7F7F7] transition-colors"
                        >
                          <HelpCircle className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                          <span className="font-medium">{language === 'nl' ? 'Hoe werkt het?' : 'How it works'}</span>
                        </button>
                        <button
                          onClick={() => { navigate('/over-ons'); setShowNavMenu(false); }}
                          className="flex items-center w-full px-4 py-3 rounded-xl text-[#2A2A2A] hover:bg-[#F7F7F7] transition-colors"
                        >
                          <Info className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                          <span className="font-medium">{language === 'nl' ? 'Over swipejewoning' : 'About us'}</span>
                        </button>
                        <button
                          onClick={() => { navigate('/verhuurder/tips'); setShowNavMenu(false); }}
                          className="flex items-center w-full px-4 py-3 rounded-xl text-[#2A2A2A] hover:bg-[#F7F7F7] transition-colors"
                        >
                          <Lightbulb className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                          <span className="font-medium">{language === 'nl' ? 'Handige tips' : 'Useful tips'}</span>
                        </button>
                        <button
                          onClick={() => { navigate('/contact'); setShowNavMenu(false); }}
                          className="flex items-center w-full px-4 py-3 rounded-xl text-[#2A2A2A] hover:bg-[#F7F7F7] transition-colors"
                        >
                          <Headphones className="w-5 h-5 mr-3 text-[#9E9E9E]" />
                          <span className="font-medium">Support</span>
                        </button>
                        <div className="border-t border-[#E5E5E5] my-2" />
                        <button
                          onClick={() => { logout(); navigate('/'); setShowNavMenu(false); }}
                          className="flex items-center w-full px-4 py-3 rounded-xl text-[#C04A4A] hover:bg-[#FEF2F2] transition-colors"
                        >
                          <LogOut className="w-5 h-5 mr-3" />
                          <span className="font-medium">{language === 'nl' ? 'Uitloggen' : 'Logout'}</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm">{language === 'nl' ? 'Welkom terug,' : 'Welcome back,'}</p>
                <h1 className="text-2xl md:text-3xl font-black text-white">{user?.name || (language === 'nl' ? 'Verhuurder' : 'Landlord')}</h1>
              </div>
              
              {/* Stats Row - Horizontal */}
              <div className="hidden sm:flex items-center gap-3">
                {[
                  { value: stats.totalListings, label: language === 'nl' ? 'Woningen' : 'Properties', icon: Building2, color: 'bg-[#2E7D32]' },
                  { value: stats.totalViews, label: 'Views', icon: Eye, color: 'bg-[#FF9800]' },
                  { value: stats.totalInterested, label: language === 'nl' ? 'Interesse' : 'Interests', icon: Heart, color: 'bg-[#C04A4A]' },
                ].map((stat, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="text-xl font-bold text-white">{stat.value}</div>
                      <div className="text-xs text-white/60">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Stats */}
            <div className="grid grid-cols-3 gap-2 mt-4 sm:hidden">
              {[
                { value: stats.totalListings, label: language === 'nl' ? 'Woningen' : 'Properties', color: 'text-[#2E7D32]' },
                { value: stats.totalViews, label: 'Views', color: 'text-[#FF9800]' },
                { value: stats.totalInterested, label: language === 'nl' ? 'Interesse' : 'Interests', color: 'text-[#C04A4A]' },
              ].map((stat, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
                  <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                  <div className="text-xs text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white border-b border-[#E5E5E5] px-4 flex-shrink-0">
          <div className="max-w-7xl mx-auto flex">
            <button
              onClick={() => setActiveTab('woningen')}
              className={`flex-1 sm:flex-none px-6 py-4 font-semibold text-sm border-b-2 transition-colors ${
                activeTab === 'woningen' 
                  ? 'border-[#2E7D32] text-[#2E7D32]' 
                  : 'border-transparent text-[#9E9E9E] hover:text-[#2A2A2A]'
              }`}
              data-testid="tab-woningen"
            >
              <Building2 className="w-4 h-4 inline mr-2" />
              {language === 'nl' ? 'Mijn woningen' : 'My properties'}
            </button>
            <button
              onClick={() => setActiveTab('berichten')}
              className={`flex-1 sm:flex-none px-6 py-4 font-semibold text-sm border-b-2 transition-colors relative ${
                activeTab === 'berichten' 
                  ? 'border-[#2E7D32] text-[#2E7D32]' 
                  : 'border-transparent text-[#9E9E9E] hover:text-[#2A2A2A]'
              }`}
              data-testid="tab-berichten"
            >
              <MessageSquare className="w-4 h-4 inline mr-2" />
              {language === 'nl' ? 'Geïnteresseerden' : 'Interested'}
              {interestedTenants.length > 0 && (
                <span className="absolute -top-1 right-2 sm:relative sm:top-auto sm:right-auto sm:ml-2 bg-[#C04A4A] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {interestedTenants.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'woningen' ? (
            /* WONINGEN TAB */
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              {/* Quick Actions */}
              <div className="mb-6 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => navigate('/verhuurder/onboarding')}
                  className="flex-1 sm:flex-none bg-[#2E7D32] hover:bg-[#256B2B] text-white h-12 px-6 rounded-xl font-bold shadow-lg shadow-[#2E7D32]/20"
                  data-testid="add-listing-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  {language === 'nl' ? 'Nieuwe woning plaatsen' : 'Add new property'}
                </Button>
                <Button
                  onClick={() => setShowProfileEdit(true)}
                  variant="outline"
                  className="flex-1 sm:flex-none h-12 px-6 rounded-xl font-bold border-[#1F3A5F] text-[#1F3A5F] hover:bg-[#1F3A5F] hover:text-white"
                  data-testid="edit-profile-btn"
                >
                  <Briefcase className="w-5 h-5 mr-2" />
                  {language === 'nl' ? 'Bedrijfsprofiel' : 'Business profile'}
                </Button>
              </div>

              {listings.length === 0 ? (
                <div className="bg-white rounded-2xl p-8 md:p-12 border border-[#E5E5E5] text-center shadow-sm">
                  <div className="w-24 h-24 bg-gradient-to-br from-[#2E7D32]/10 to-[#1F3A5F]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Building2 className="w-12 h-12 text-[#2E7D32]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#2A2A2A] mb-2">{language === 'nl' ? 'Nog geen advertenties' : 'No listings yet'}</h3>
                  <p className="text-[#9E9E9E] mb-8 max-w-md mx-auto">
                    {language === 'nl' 
                      ? 'Plaats je eerste woning en bereik duizenden actieve woningzoekers. Het plaatsen is volledig gratis!'
                      : 'List your first property and reach thousands of active home seekers. Listing is completely free!'
                    }
                  </p>
                  <Button 
                    onClick={() => navigate('/verhuurder/onboarding')}
                    className="bg-[#2E7D32] hover:bg-[#256B2B] text-white h-14 px-8 text-lg font-bold rounded-xl shadow-lg shadow-[#2E7D32]/20"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    {language === 'nl' ? 'Gratis woning plaatsen' : 'List property for free'}
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4 md:gap-6">
                  {listings.map((listing) => (
                    <div 
                      key={listing.id}
                      className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden hover:shadow-xl transition-all shadow-sm group"
                      data-testid={`listing-card-${listing.id}`}
                    >
                      <div className="flex flex-col sm:flex-row">
                        <div className="w-full sm:w-48 md:w-64 h-48 sm:h-auto flex-shrink-0 relative overflow-hidden">
                          <img 
                            src={getImageUrl(listing.images?.[0])}
                            alt={listing.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold ${
                            listing.active 
                              ? 'bg-[#2E7D32] text-white' 
                              : 'bg-[#9E9E9E] text-white'
                          }`}>
                            {listing.active ? (language === 'nl' ? 'ACTIEF' : 'ACTIVE') : (language === 'nl' ? 'GEPAUZEERD' : 'PAUSED')}
                          </div>
                        </div>
                        
                        <div className="flex-1 p-4 md:p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg md:text-xl font-bold text-[#2A2A2A] mb-1 group-hover:text-[#2E7D32] transition-colors">{listing.title}</h3>
                              <div className="flex items-center gap-1 text-[#9E9E9E] text-sm mb-3">
                                <MapPin className="w-4 h-4" />
                                <span>{listing.neighborhood}, {listing.city}</span>
                              </div>
                              
                              <div className="flex flex-wrap items-center gap-4 mb-4">
                                <div className="flex items-center gap-1.5 bg-[#F7F7F7] px-3 py-1.5 rounded-full">
                                  <Eye className="w-4 h-4 text-[#FF9800]" />
                                  <span className="text-sm font-medium text-[#2A2A2A]">{listing.views || 0} views</span>
                                </div>
                                <div className="flex items-center gap-1.5 bg-[#F7F7F7] px-3 py-1.5 rounded-full">
                                  <Heart className="w-4 h-4 text-[#C04A4A]" />
                                  <span className="text-sm font-medium text-[#2A2A2A]">{listing.reactionCount || 0} {language === 'nl' ? 'interesse' : 'interests'}</span>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-3">
                                <span className="text-2xl md:text-3xl font-black text-[#2E7D32]">€{listing.price}</span>
                                <span className="text-[#9E9E9E]">/{language === 'nl' ? 'maand' : 'month'}</span>
                              </div>
                              
                              {/* Action buttons - always visible */}
                              <div className="flex items-center gap-2 mt-4">
                                <button 
                                  onClick={() => navigate(`/woning/${listing.id}`)}
                                  className="flex-1 h-10 bg-[#F7F7F7] hover:bg-[#E5E5E5] rounded-xl flex items-center justify-center gap-2 text-sm font-medium text-[#2A2A2A] transition-colors"
                                >
                                  <Eye className="w-4 h-4" />
                                  {language === 'nl' ? 'Bekijken' : 'View'}
                                </button>
                                <button 
                                  onClick={() => navigate(`/verhuurder/woning/${listing.id}/bewerk`)}
                                  className="flex-1 h-10 bg-[#2E7D32] hover:bg-[#256B2B] rounded-xl flex items-center justify-center gap-2 text-sm font-bold text-white transition-colors"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  {language === 'nl' ? 'Bewerken' : 'Edit'}
                                </button>
                                <button 
                                  onClick={() => setShowMenu(showMenu === listing.id ? null : listing.id)}
                                  className="w-10 h-10 bg-[#F7F7F7] hover:bg-[#E5E5E5] rounded-xl flex items-center justify-center transition-colors"
                                >
                                  <MoreVertical className="w-5 h-5 text-[#9E9E9E]" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Dropdown menu for more options */}
                            <div className="relative">
                              {showMenu === listing.id && (
                                <div className="absolute right-0 top-0 bg-white rounded-xl shadow-xl border border-[#E5E5E5] py-2 w-48 z-10">
                                  <button 
                                    onClick={() => { toggleListingActive(listing.id, listing.active); setShowMenu(null); }}
                                    className="w-full px-4 py-3 text-left text-sm hover:bg-[#F7F7F7] flex items-center gap-3"
                                  >
                                    {listing.active ? <ToggleRight className="w-4 h-4 text-[#FF9800]" /> : <ToggleLeft className="w-4 h-4 text-[#2E7D32]" />}
                                    {listing.active ? (language === 'nl' ? 'Pauzeren' : 'Pause') : (language === 'nl' ? 'Activeren' : 'Activate')}
                                  </button>
                                  <hr className="my-2 border-[#E5E5E5]" />
                                  <button 
                                    onClick={() => { deleteListing(listing.id); setShowMenu(null); }}
                                    className="w-full px-4 py-3 text-left text-sm hover:bg-[#F7F7F7] flex items-center gap-3 text-[#C04A4A]"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    {language === 'nl' ? 'Verwijderen' : 'Delete'}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* BERICHTEN TAB - Listing-first view */
            <div className="max-w-3xl mx-auto px-4 py-6">
              {!selectedListingForMessages ? (
                /* Step 1: Show all listings with interest counts */
                <>
                  {listingsWithInterest.length === 0 || listingsWithInterest.every(l => l.interestedCount === 0) ? (
                    <div className="bg-white rounded-2xl p-8 md:p-12 border border-[#E5E5E5] text-center shadow-sm">
                      <div className="w-24 h-24 bg-gradient-to-br from-[#2E7D32]/10 to-[#1F3A5F]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <Users className="w-12 h-12 text-[#2E7D32]" />
                      </div>
                      <h3 className="text-2xl font-bold text-[#2A2A2A] mb-2">{language === 'nl' ? 'Nog geen geïnteresseerden' : 'No interested tenants yet'}</h3>
                      <p className="text-[#9E9E9E] mb-8 max-w-md mx-auto">
                        {language === 'nl' 
                          ? 'Zodra huurders interesse tonen in je woning(en), verschijnen ze hier. Je kunt dan direct contact opnemen!'
                          : 'Once renters show interest in your property(s), they will appear here. You can then contact them directly!'
                        }
                      </p>
                      {listings.length === 0 && (
                        <Button 
                          onClick={() => navigate('/verhuurder/onboarding')}
                          className="bg-[#2E7D32] hover:bg-[#256B2B] text-white h-12 px-6 font-bold rounded-xl"
                        >
                          <Plus className="w-5 h-5 mr-2" />
                          {language === 'nl' ? 'Plaats een woning' : 'Add a property'}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between mb-2">
                        <h2 className="text-xl font-bold text-[#2A2A2A]">
                          {language === 'nl' ? 'Geïnteresseerden per woning' : 'Interested per listing'}
                        </h2>
                        <span className="text-sm text-[#9E9E9E]">{stats.totalInterested} totaal</span>
                      </div>
                      
                      {listingsWithInterest.filter(l => l.interestedCount > 0).map((listing) => (
                        <button
                          key={listing.id}
                          onClick={() => setSelectedListingForMessages(listing)}
                          className="w-full bg-white rounded-xl border border-[#E5E5E5] overflow-hidden hover:shadow-lg hover:border-[#2E7D32]/30 transition-all text-left"
                          data-testid={`listing-messages-${listing.id}`}
                        >
                          <div className="flex items-center gap-4 p-4">
                            {/* Listing Image */}
                            <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                              <img 
                                src={getImageUrl(listing.images?.[0])}
                                alt={listing.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            
                            {/* Listing Info */}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-[#2A2A2A] truncate">{listing.title}</h3>
                              <div className="flex items-center gap-2 text-sm text-[#9E9E9E] mt-1">
                                <MapPin className="w-3.5 h-3.5" />
                                <span>{listing.city}</span>
                                <span>•</span>
                                <span>€{listing.price}/mnd</span>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <div className="flex items-center gap-1.5 bg-[#2E7D32]/10 text-[#2E7D32] px-3 py-1 rounded-full">
                                  <Heart className="w-4 h-4 fill-current" />
                                  <span className="font-bold text-sm">{listing.interestedCount}</span>
                                  <span className="text-xs">{listing.interestedCount === 1 ? 'like' : 'likes'}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Arrow */}
                            <ChevronRight className="w-6 h-6 text-[#9E9E9E]" />
                          </div>
                        </button>
                      ))}
                      
                      {/* Listings without interest */}
                      {listingsWithInterest.some(l => l.interestedCount === 0) && (
                        <div className="mt-6">
                          <p className="text-sm text-[#9E9E9E] mb-3">{language === 'nl' ? 'Nog geen likes:' : 'No likes yet:'}</p>
                          {listingsWithInterest.filter(l => l.interestedCount === 0).map((listing) => (
                            <div
                              key={listing.id}
                              className="bg-[#F7F7F7] rounded-xl p-3 mb-2 flex items-center gap-3 opacity-60"
                            >
                              <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                                <img 
                                  src={getImageUrl(listing.images?.[0])}
                                  alt={listing.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-[#2A2A2A] truncate text-sm">{listing.title}</p>
                                <p className="text-xs text-[#9E9E9E]">{listing.city} • €{listing.price}/mnd</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                /* Step 2: Show chats for selected listing */
                <div>
                  {/* Back button and listing header */}
                  <button
                    onClick={() => { setSelectedListingForMessages(null); setSelectedTenant(null); }}
                    className="flex items-center gap-2 text-[#2E7D32] font-medium mb-4 hover:underline"
                  >
                    <ChevronRight className="w-5 h-5 rotate-180" />
                    {language === 'nl' ? 'Terug naar overzicht' : 'Back to overview'}
                  </button>
                  
                  {/* Selected listing info */}
                  <div className="bg-white rounded-xl border border-[#E5E5E5] p-4 mb-4 flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                      <img 
                        src={getImageUrl(selectedListingForMessages.images?.[0])}
                        alt={selectedListingForMessages.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-[#2A2A2A]">{selectedListingForMessages.title}</h3>
                      <p className="text-sm text-[#9E9E9E]">{selectedListingForMessages.city} • €{selectedListingForMessages.price}/mnd</p>
                    </div>
                    <div className="bg-[#2E7D32]/10 text-[#2E7D32] px-3 py-1 rounded-full flex items-center gap-1.5">
                      <Heart className="w-4 h-4 fill-current" />
                      <span className="font-bold">{selectedListingForMessages.interestedCount}</span>
                    </div>
                  </div>
                  
                  {/* Interested tenants for this listing */}
                  <h4 className="font-bold text-[#2A2A2A] mb-3">
                    {language === 'nl' ? 'Geïnteresseerde huurders' : 'Interested tenants'} ({selectedListingForMessages.interested?.length || 0})
                  </h4>
                  
                  <div className="space-y-3">
                    {selectedListingForMessages.interested?.map((tenant) => (
                      <div
                        key={tenant.id}
                        className="bg-white rounded-xl border border-[#E5E5E5] overflow-hidden hover:shadow-lg hover:border-[#2E7D32]/30 transition-all"
                        data-testid={`tenant-card-${tenant.tenant?.id}`}
                      >
                        <div className="p-4">
                          <div className="flex items-start gap-4">
                            {/* Avatar with verified badge */}
                            <div className="relative flex-shrink-0">
                              <div className="w-14 h-14 bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F] rounded-full flex items-center justify-center text-white font-bold text-xl">
                                {tenant.tenant?.avatar || tenant.tenant?.name?.charAt(0).toUpperCase() || 'H'}
                              </div>
                              {tenant.tenant?.isPremium && (
                                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                                  <CheckCircle className="w-3.5 h-3.5 text-white" />
                                </div>
                              )}
                            </div>
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-bold text-[#2A2A2A] truncate">{tenant.tenant?.name}</span>
                                  {tenant.tenant?.isPremium && (
                                    <span className="inline-flex items-center gap-1 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0">
                                      <CheckCircle className="w-3 h-3" />
                                      {language === 'nl' ? 'Geverifieerd' : 'Verified'}
                                    </span>
                                  )}
                                </div>
                                <span className="text-xs text-[#9E9E9E] bg-[#F7F7F7] px-2 py-1 rounded-full flex-shrink-0 ml-2">
                                  {formatTime(tenant.matchedAt)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-[#2E7D32] font-medium">{tenant.tenant?.profileType}</span>
                                {tenant.tenant?.budget && (
                                  <>
                                    <span className="text-[#E5E5E5]">•</span>
                                    <span className="text-[#9E9E9E]">{tenant.tenant.budget}</span>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Heart */}
                            <Heart className="w-5 h-5 text-[#2E7D32] fill-[#2E7D32] flex-shrink-0 mt-4" />
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#E5E5E5]">
                            <button
                              onClick={() => setViewingTenant(tenant)}
                              className="flex-1 h-9 bg-[#F7F7F7] hover:bg-[#E5E5E5] rounded-lg flex items-center justify-center gap-1.5 text-xs font-medium text-[#2A2A2A] transition-colors"
                              data-testid="view-profile-btn"
                            >
                              <User className="w-3.5 h-3.5" />
                              {language === 'nl' ? 'Profiel' : 'Profile'}
                            </button>
                            <button
                              onClick={() => loadChat(tenant)}
                              className="flex-1 h-9 bg-[#2E7D32] hover:bg-[#256B2B] rounded-lg flex items-center justify-center gap-1.5 text-xs font-bold text-white transition-colors"
                              data-testid="open-chat-btn"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                              {language === 'nl' ? 'Chat' : 'Chat'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tenant Profile Modal */}
      {viewingTenant && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setViewingTenant(null)}>
          <div 
            className="bg-white rounded-3xl max-w-md w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Profile Header */}
            <div className="bg-gradient-to-br from-[#1F3A5F] to-[#2E7D32] p-8 text-center relative rounded-t-3xl">
              <button 
                onClick={() => setViewingTenant(null)}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="relative inline-block">
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-4xl font-bold text-[#1F3A5F]">
                    {viewingTenant.tenant?.name?.charAt(0).toUpperCase() || 'H'}
                  </span>
                </div>
                {viewingTenant.tenant?.isPremium && (
                  <div className="absolute bottom-2 right-0 w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center border-3 border-white shadow-lg">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-1">{viewingTenant.tenant?.name}</h2>
              
              {/* Verified Badge */}
              {viewingTenant.tenant?.isPremium && (
                <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#FFD700] to-[#FFA500] px-4 py-1.5 rounded-full mb-3 shadow-lg">
                  <CheckCircle className="w-4 h-4 text-white" />
                  <span className="text-sm font-bold text-white">{language === 'nl' ? 'Geverifieerde Huurder' : 'Verified Tenant'}</span>
                </div>
              )}
              
              <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                <User className="w-4 h-4 text-white" />
                <span className="text-sm text-white capitalize">{viewingTenant.tenant?.profileType}</span>
              </div>
            </div>

            {/* Profile Details */}
            <div className="p-6 space-y-4">
              {/* Budget */}
              {viewingTenant.tenant?.budget && (
                <div className="flex items-center gap-3 p-3 bg-[#F7F7F7] rounded-xl">
                  <div className="w-10 h-10 bg-[#2E7D32]/10 rounded-lg flex items-center justify-center">
                    <Euro className="w-5 h-5 text-[#2E7D32]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#9E9E9E]">Budget</p>
                    <p className="font-bold text-[#2A2A2A]">{viewingTenant.tenant.budget}</p>
                  </div>
                </div>
              )}

              {/* Cities */}
              {viewingTenant.tenant?.cities?.length > 0 && (
                <div className="flex items-center gap-3 p-3 bg-[#F7F7F7] rounded-xl">
                  <div className="w-10 h-10 bg-[#1F3A5F]/10 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#1F3A5F]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'Zoekt in' : 'Looking in'}</p>
                    <p className="font-bold text-[#2A2A2A]">{viewingTenant.tenant.cities.join(', ')}</p>
                  </div>
                </div>
              )}

              {/* Interest in listing */}
              {viewingTenant.listing && (
                <div className="flex items-center gap-3 p-3 bg-[#F7F7F7] rounded-xl">
                  <div className="w-10 h-10 bg-[#FF9800]/10 rounded-lg flex items-center justify-center">
                    <Home className="w-5 h-5 text-[#FF9800]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'Interesse in' : 'Interested in'}</p>
                    <p className="font-bold text-[#2A2A2A]">{viewingTenant.listing.title}</p>
                  </div>
                </div>
              )}

              {/* Action Button */}
              <button
                onClick={() => { setViewingTenant(null); loadChat(viewingTenant); }}
                className="w-full h-12 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors"
              >
                <MessageSquare className="w-5 h-5" />
                {language === 'nl' ? 'Start chat' : 'Start chat'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Edit Modal */}
      {showProfileEdit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowProfileEdit(false)}>
          <div 
            className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1F3A5F] to-[#2E7D32] p-6 text-white rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">{language === 'nl' ? 'Bedrijfsprofiel bewerken' : 'Edit business profile'}</h2>
                <button 
                  onClick={() => setShowProfileEdit(false)}
                  className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-white/80 text-sm mt-1">{language === 'nl' ? 'Deze info is zichtbaar voor huurders' : 'This info is visible to renters'}</p>
            </div>
            
            {/* Form */}
            <div className="p-6 space-y-4">
              {/* Avatar Upload */}
              <div className="flex flex-col items-center mb-4">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#1F3A5F] to-[#2E7D32] flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                    {profileData.avatar ? (
                      <img 
                        src={profileData.avatar} 
                        alt="Avatar" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-3xl font-bold text-white">
                        {user?.name?.charAt(0).toUpperCase() || 'V'}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-[#2E7D32] rounded-full flex items-center justify-center text-white hover:bg-[#256B2B] transition-colors shadow-lg"
                  >
                    {uploadingAvatar ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                  </button>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                  />
                </div>
                <p className="text-xs text-[#9E9E9E] mt-2">{language === 'nl' ? 'Klik op het camera-icoon om een foto te uploaden' : 'Click the camera icon to upload a photo'}</p>
              </div>
              
              {/* Business Name */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-[#1F3A5F]" />
                  {language === 'nl' ? 'Bedrijfsnaam' : 'Business name'}
                </label>
                <Input
                  value={profileData.businessName}
                  onChange={(e) => setProfileData(prev => ({ ...prev, businessName: e.target.value }))}
                  placeholder={language === 'nl' ? 'bijv. Van der Berg Vastgoed' : 'e.g. Van der Berg Real Estate'}
                  className="h-12 rounded-xl border-[#E5E5E5]"
                />
                <p className="text-xs text-[#9E9E9E] mt-1">{language === 'nl' ? 'Optioneel - alleen invullen als je een bedrijf hebt' : 'Optional - only fill in if you have a company'}</p>
              </div>
              
              {/* KVK Number */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#2E7D32]" />
                  {language === 'nl' ? 'KVK-nummer' : 'Chamber of Commerce number'}
                </label>
                <Input
                  value={profileData.kvkNumber}
                  onChange={(e) => setProfileData(prev => ({ ...prev, kvkNumber: e.target.value }))}
                  placeholder="12345678"
                  className="h-12 rounded-xl border-[#E5E5E5]"
                  maxLength={8}
                />
                <p className="text-xs text-[#9E9E9E] mt-1">{language === 'nl' ? '8 cijfers - verhoogt vertrouwen bij huurders' : '8 digits - increases trust with renters'}</p>
              </div>
              
              {/* Bio */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
                  <User className="w-4 h-4 text-[#1F3A5F]" />
                  {language === 'nl' ? 'Over jezelf' : 'About yourself'}
                </label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder={language === 'nl' ? 'Vertel iets over jezelf als verhuurder...' : 'Tell something about yourself as a landlord...'}
                  className="w-full min-h-[100px] p-3 rounded-xl border border-[#E5E5E5] resize-none text-sm"
                  maxLength={300}
                />
                <p className="text-xs text-[#9E9E9E] mt-1">{profileData.bio.length}/300 {language === 'nl' ? 'tekens' : 'characters'}</p>
              </div>
              
              {/* Info box */}
              <div className="bg-[#2E7D32]/5 rounded-xl p-4 border border-[#2E7D32]/20">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-[#2A2A2A]">{language === 'nl' ? 'Waarom invullen?' : 'Why fill this in?'}</p>
                    <p className="text-xs text-[#9E9E9E] mt-1">{language === 'nl' ? 'Verhuurders met een compleet profiel krijgen 2x meer reacties van serieuze huurders.' : 'Landlords with a complete profile get 2x more responses from serious renters.'}</p>
                  </div>
                </div>
              </div>
              
              {/* Save Button */}
              <Button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="w-full h-12 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl font-bold"
              >
                {savingProfile ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {language === 'nl' ? 'Opslaan...' : 'Saving...'}
                  </>
                ) : (
                  language === 'nl' ? 'Profiel opslaan' : 'Save profile'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation - Mobile only */}
      <div className={`bg-white border-t border-[#E5E5E5] px-4 py-3 safe-area-bottom lg:hidden flex-shrink-0 ${selectedTenant ? 'hidden' : ''}`}>
        <div className="max-w-lg mx-auto flex justify-around">
          {[
            { icon: Building2, label: language === 'nl' ? 'Woningen' : 'Properties', active: activeTab === 'woningen', action: () => setActiveTab('woningen') },
            { icon: MessageSquare, label: language === 'nl' ? 'Berichten' : 'Messages', active: activeTab === 'berichten', action: () => setActiveTab('berichten'), badge: interestedTenants.length },
            { icon: Plus, label: language === 'nl' ? 'Nieuw' : 'New', active: false, action: () => navigate('/verhuurder/onboarding'), primary: true },
            { icon: Settings, label: language === 'nl' ? 'Account' : 'Account', active: false, action: () => navigate('/verhuurder/account') }
          ].map((item, i) => (
            <button 
              key={i}
              onClick={item.action}
              className={`flex flex-col items-center gap-1 px-4 py-1 relative ${
                item.primary ? '' : item.active ? 'text-[#2E7D32]' : 'text-[#9E9E9E]'
              }`}
            >
              {item.primary ? (
                <div className="w-12 h-12 bg-[#2E7D32] rounded-full flex items-center justify-center -mt-4 shadow-lg">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
              ) : (
                <>
                  <item.icon className="w-5 h-5" />
                  {item.badge > 0 && (
                    <span className="absolute -top-1 right-2 bg-[#C04A4A] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </>
              )}
              <span className="text-xs font-medium">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VerhuurderDashboardPage;
